import { FC, memo, useState, useEffect } from 'react'
import { ActiveFilter } from '../types'
import { ExploreColumn } from './ExploreColumn'
import { Tabs } from './Tabs'
import { TaskColumn } from './TaskColumn'

interface Props {
  activeTab?: 'explore' | 'tasks'
  disabledTaskIds: Set<number>
  displaySearch?: boolean
  filter?: ActiveFilter
  heading?: string
  integrationId: number
  onActiveFilterChange: (filter: ActiveFilter) => void
  onItemClick: (item: { label: string }) => void
  onSearchChange: (search: string) => void
  onSearchToggle: (displayed: boolean) => void
  onTabChange: (tab: 'explore' | 'tasks') => void
  onTaskClick: (task: Task, options: { selected: boolean }) => void
  path: string[]
  projectId: number
  search?: string
  searchError?: string
  selectedTaskIds: Set<number>
  shown: boolean
}

export const NodeColumn: FC<Props> = memo(
  ({
    activeTab = 'explore',
    disabledTaskIds,
    displaySearch,
    filter,
    heading,
    integrationId,
    onActiveFilterChange,
    onItemClick,
    onSearchChange,
    onSearchToggle,
    onTabChange,
    onTaskClick,
    path,
    projectId,
    search,
    searchError,
    selectedTaskIds,
    shown,
  }) => {
    const [exploreCount, setExploreCount] = useState<number | undefined>(
      undefined,
    )
    const [taskCount, setTaskCount] = useState<number | undefined>(undefined)
    const [consideredAutoSwitchToTaskTab, setConsideredAutoSwitchToTaskTab] =
      useState(false)

    // The path effectively defines the column,
    // so we can hook into path changes to effectively
    // identify when a "new" column is being shown.
    useEffect(() => {
      // When a new column is loaded, consider switching
      // to the Task tab automatically once the counts of each tab are loaded.
      setConsideredAutoSwitchToTaskTab(false)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(path)])

    // If we haven't already considered switching to the Task tab,
    // and counts for both tabs have loaded,
    // and the explore tab has no items but the task tab does have items,
    // switch to the task tab automatically.
    // If both counts load and the other conditions are not met,
    // update state to reflect that we considered and declined
    // so that we do not change tabs again until the column changes.
    useEffect(() => {
      if (!consideredAutoSwitchToTaskTab) {
        // If explore is 0 and task is loaded and has items, switch to the task tab
        if (exploreCount === 0 && taskCount !== undefined && taskCount > 0) {
          onTabChange('tasks')
        }

        // If both counts have loaded, update state to reflect that we considered
        // switching automatically
        if (exploreCount !== undefined && taskCount !== undefined) {
          setConsideredAutoSwitchToTaskTab(true)
        }
      }
    }, [consideredAutoSwitchToTaskTab, exploreCount, taskCount, onTabChange])

    return (
      <>
        <ExploreColumn
          displaySearch={displaySearch}
          filter={filter}
          heading={heading}
          integrationId={integrationId}
          onActiveFilterChange={onActiveFilterChange}
          onItemClick={onItemClick}
          onItemCountChange={setExploreCount}
          onSearchChange={onSearchChange}
          onSearchToggle={onSearchToggle}
          path={path}
          projectId={projectId}
          search={search}
          searchError={searchError}
          shown={shown && activeTab === 'explore'}
        >
          <Tabs
            activeTab="explore"
            exploreCount={exploreCount}
            onTabChange={onTabChange}
            taskCount={taskCount}
          />
        </ExploreColumn>
        <TaskColumn
          disabledTaskIds={disabledTaskIds}
          displaySearch={displaySearch}
          filter={filter}
          heading={heading}
          integrationId={integrationId}
          onActiveFilterChange={onActiveFilterChange}
          onItemCountChange={setTaskCount}
          onSearchChange={onSearchChange}
          onSearchToggle={onSearchToggle}
          onTaskClick={onTaskClick}
          path={path}
          projectId={projectId}
          search={search}
          searchError={searchError}
          selectedTaskIds={selectedTaskIds}
          shown={shown && activeTab === 'tasks'}
        >
          <Tabs
            activeTab="tasks"
            exploreCount={exploreCount}
            onTabChange={onTabChange}
            taskCount={taskCount}
          />
        </TaskColumn>
      </>
    )
  },
)

NodeColumn.displayName = 'NodeColumn'
